import GraphItemLoader from "@/components/common/GraphItem/GraphItemLoader";
import BlockTemplateDialog from "@/components/pages/home/BlockTemplateDialog";
import { Button } from "@/components/ui/button";
import { sizeOptions } from "@/puck/options";
import AuthContext from "@/store/contexts/AuthContext";
import { ComponentConfig } from "@measured/puck";
import { BookTemplate } from "lucide-react";
import { useContext, useRef } from "react";

export type GraphProps = {
  configuration: string;
  gridColumnSpan: number;
  gridRowSpan: number;
};

const GraphConfiguration = ({ id }: { id: string }) => {
  const { openGraphConfiguration } = useContext(AuthContext);
  return (
    <div className="flex flex-col items-center gap-2">
      <Button
        className="w-full"
        onClick={() =>
          openGraphConfiguration({ id: id.split("_")[0], type: "graph" })
        }
      >
        Update graph configuration
      </Button>
      <span>or</span>
      <BlockTemplateDialog id={id.split("_")[0]} blockType="graph">
        <Button className="w-full" size="lg" variant={"outline"}>
          <BookTemplate />
          Use a graph template
        </Button>
      </BlockTemplateDialog>
    </div>
  );
};

export const Graph: ComponentConfig<GraphProps> = {
  label: "Graph",
  fields: {
    gridColumnSpan: {
      type: "select",
      label: "Width",
      options: sizeOptions,
    },
    gridRowSpan: {
      type: "select",
      label: "Height",
      options: sizeOptions,
    },
    configuration: {
      type: "custom",
      render: ({ id }) => {
        return <GraphConfiguration id={id} />;
      },
    },
  },
  defaultProps: {
    configuration: "",
    gridColumnSpan: 2,
    gridRowSpan: 2,
  },
  render: ({ id, gridColumnSpan, gridRowSpan, puck }) => {
    const itemRef = useRef<HTMLDivElement>(null);

    const col = [
      "col-span-1",
      "col-span-2",
      "col-span-2 xl:col-span-3",
      "col-span-2 xl:col-span-4",
    ];
    const row = ["row-span-1", "row-span-2", "row-span-3", "row-span-4"];

    const style = {
      height: `${150 * gridRowSpan}px`,
    };

    if (puck.isEditing && itemRef.current?.parentElement) {
      for (let index = 0; index < col.length; index++) {
        const colSpan = col[index];
        itemRef.current.parentElement.classList.remove(...colSpan.split(" "));
      }

      for (let index = 0; index < row.length; index++) {
        const rowSpan = row[index];
        itemRef.current.parentElement.classList.remove(rowSpan);
      }

      itemRef.current.parentElement.classList.add(
        ...col[gridColumnSpan - 1].split(" ")
      );
      itemRef.current.parentElement.classList.add(row[gridRowSpan - 1]);
    }

    return (
      <div
        ref={itemRef}
        id={id}
        style={style}
        className={`${col[gridColumnSpan - 1]} ${
          row[gridRowSpan - 1]
        } page-break`}
      >
        <GraphItemLoader id={id} editing={puck.isEditing} />
      </div>
    );
  },
};
